'use client'

import CatalogMediaGallery from '@/components/catalog/catalog-media-gallery'
import { stripHtml } from '@/lib/utils/strip-html'
import {
    Catalog,
    CatalogPartner,
    CatalogProfessional,
    CatalogVariation,
    CatalogExam,
    getCatalog,
    addCatalogExam,
    updateCatalogExam,
    removeCatalogExam,
    getCatalogsByCompany,
    catalogCompanyPrice,
    catalogBasePrice,
} from '@/lib/api/catalog/catalog-api'
import ConfirmModal from '@/components/confirm-modal'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdEdit,
    MdEvent,
    MdAccessTime,
    MdAttachMoney,
    MdBusiness,
    MdPerson,
    MdDescription,
    MdLocalHospital,
    MdCategory,
    MdCheckCircle,
    MdClose,
    MdInfo,
    MdScience,
    MdVideocam,
    MdAdd,
    MdDelete,
    MdCardGiftcard,
    MdLocationOn,
    MdTune,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

function formatPrice(cents: number): string {
    return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
}

function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m > 0 ? `${h}h ${m}min` : `${h}h`
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
    CONSULTATION: <MdLocalHospital className="text-lg" />,
    EXAM: <MdScience className="text-lg" />,
    PROCEDURE: <MdDescription className="text-lg" />,
    TELECONSULTATION: <MdVideocam className="text-lg" />,
}

const CATEGORY_COLORS: Record<string, string> = {
    CONSULTATION: 'badge-secondary',
    EXAM: 'badge-info',
    PROCEDURE: 'badge-warning',
    TELECONSULTATION: 'badge-secondary',
    PACKAGE: 'badge-success',
}

export default function CatalogDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canUpdate } = useResourcePermissions('registrations', 'catalog')
    // Guia é documento de convênio: a segunda oração só faz sentido na saúde.
    const { isHealth } = useServiceLabel()
    const categoryLabels = useCatalogCategoryLabels()

    const [catalog, setCatalog] = useState<Catalog | null>(null)
    const [loading, setLoading] = useState(true)
    const [examSearch, setExamSearch] = useState('')
    const [examResults, setExamResults] = useState<Catalog[]>([])
    const [searchingExams, setSearchingExams] = useState(false)
    const [addingExam, setAddingExam] = useState(false)
    const [removingExamId, setRemovingExamId] = useState<number | null>(null)
    const [confirmRemoveExam, setConfirmRemoveExam] = useState<CatalogExam | null>(null)

    useEffect(() => {
        if (id) loadCatalog()
    }, [id])

    const loadCatalog = async () => {
        setLoading(true)
        try {
            const response = await getCatalog(parseInt(id!, 10))
            if (response.success) {
                setCatalog(response.result)
            } else {
                toast.error('Erro ao carregar item do catálogo')
                navigate('/registrations/catalog')
            }
        } catch {
            toast.error('Erro ao carregar item do catálogo')
        } finally {
            setLoading(false)
        }
    }

    const searchExams = async (term: string) => {
        setExamSearch(term)
        if (term.length < 2 || !catalog) {
            setExamResults([])
            return
        }
        setSearchingExams(true)
        try {
            const response = await getCatalogsByCompany(catalog.companyId, {
                category: 'EXAM',
                search: term,
                activeOnly: true,
            })
            if (response.success) {
                const existingIds = new Set(catalog.catalogExams?.map(ce => ce.examCatalogId) || [])
                setExamResults((response.result || []).filter(e => !existingIds.has(e.id)))
            }
        } catch {
            // ignore
        } finally {
            setSearchingExams(false)
        }
    }

    const handleAddExam = async (examCatalogId: number) => {
        if (!catalog) return
        setAddingExam(true)
        try {
            const response = await addCatalogExam(catalog.id, { examCatalogId })
            if (response.success) {
                toast.success('Exame atrelado com sucesso')
                setExamSearch('')
                setExamResults([])
                loadCatalog()
            } else {
                toast.error('Erro ao atrelar exame')
            }
        } catch {
            toast.error('Erro ao atrelar exame')
        } finally {
            setAddingExam(false)
        }
    }

    const handleToggleComplimentary = async (exam: CatalogExam) => {
        if (!catalog) return
        try {
            const response = await updateCatalogExam(catalog.id, exam.examCatalogId, {
                complimentary: !exam.complimentary,
            })
            if (response.success) {
                toast.success(exam.complimentary ? 'Exame marcado como cobrado' : 'Exame marcado como cortesia')
                loadCatalog()
            }
        } catch {
            toast.error('Erro ao atualizar exame')
        }
    }

    const handleRemoveExam = async () => {
        if (!catalog || !confirmRemoveExam) return
        setRemovingExamId(confirmRemoveExam.examCatalogId)
        try {
            const response = await removeCatalogExam(catalog.id, confirmRemoveExam.examCatalogId)
            if (response.success) {
                toast.success('Exame desatrelado com sucesso')
                loadCatalog()
            } else {
                toast.error('Erro ao remover exame')
            }
        } catch {
            toast.error('Erro ao remover exame')
        } finally {
            setRemovingExamId(null)
            setConfirmRemoveExam(null)
        }
    }

    if (loading) {
        return (
            <div className="p-6 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-lg" />
                    <div className="skeleton h-8 w-64" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="skeleton h-48 rounded-xl" />
                        <div className="skeleton h-32 rounded-xl" />
                    </div>
                    <div className="skeleton h-64 rounded-xl" />
                </div>
            </div>
        )
    }

    if (!catalog) return null

    const hasPartners = catalog.partners && catalog.partners.length > 0
    const hasProfessionals = catalog.professionals && catalog.professionals.length > 0
    const activePartners = catalog.partners?.filter(p => p.active) || []
    const activeProfessionals = catalog.professionals?.filter(p => p.active) || []
    const hasVariations = catalog.variationChildren && catalog.variationChildren.length > 0
    const activeVariations = catalog.variationChildren?.filter((v: Catalog) => v.active) || []
    // "Informações Gerais" só aparece se houver algum dado a exibir (pacotes
    // costumam não ter TUSS/duração/especialidade/instruções).
    const hasGeneralInfo =
        !!catalog.description ||
        !!catalog.tussCode ||
        !!catalog.defaultDuration ||
        !!(catalog.specialties?.length || catalog.specialty) ||
        !!catalog.instructions ||
        !!catalog.preInstructions ||
        !!catalog.postInstructions ||
        catalog.category === 'TELECONSULTATION'

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/registrations/catalog')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${catalog.active ? 'bg-secondary/10 text-secondary' : 'bg-base-300 text-base-content/50'}`}>
                            {CATEGORY_ICONS[catalog.category] || <MdCategory className="text-lg" />}
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-base-content">{catalog.name}</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`badge badge-sm ${CATEGORY_COLORS[catalog.category] || 'badge-ghost'}`}>
                                    {categoryLabels[catalog.category]}
                                </span>
                                {catalog.catalogType && (
                                    <span className="badge badge-sm badge-outline">{catalog.catalogType.name}</span>
                                )}
                                <span className={`badge badge-sm ${catalog.active ? 'badge-success' : 'badge-ghost'}`}>
                                    {catalog.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-info gap-1"
                        onClick={() => navigate(`/atendimento/scheduling?type=${catalog.category}&catalogId=${catalog.id}`)}
                    >
                        <MdEvent size={16} /> Agendar
                    </button>
                    {canUpdate && (
                        <button
                            className="btn btn-sm btn-outline btn-secondary gap-1"
                            onClick={() => navigate(`/registrations/catalog/${catalog.id}`)}
                        >
                            <MdEdit className="text-base" />
                            Editar
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Coluna principal */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Galeria. Vem antes das informações porque é o que identifica o
                        serviço num relance — e some por completo quando não há mídia,
                        para não deixar um bloco vazio no topo dos itens sem foto. */}
                    <CatalogMediaGallery media={catalog.media ?? []} />

                    {/* Informações gerais */}
                    {hasGeneralInfo && (
                    <div className="card bg-base-100 border border-base-300/50">
                        <div className="card-body">
                            <h2 className="card-title text-base">Informações Gerais</h2>

                            {catalog.description && (
                                <div className="mt-2">
                                    <label className="text-sm font-medium text-base-content/60">Descrição</label>
                                    <p className="text-sm mt-1 whitespace-pre-wrap">
                                        {stripHtml(catalog.description)}
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                                {catalog.tussCode && (
                                    <div>
                                        <label className="text-xs font-medium text-base-content/50">Código TUSS</label>
                                        <p className="text-sm font-mono font-semibold">{catalog.tussCode}</p>
                                    </div>
                                )}
                                {catalog.defaultDuration && (
                                    <div>
                                        <label className="text-xs font-medium text-base-content/50">Duração Padrão</label>
                                        <p className="text-sm font-semibold flex items-center gap-1">
                                            <MdAccessTime className="text-base text-base-content/50" />
                                            {formatDuration(catalog.defaultDuration)}
                                        </p>
                                    </div>
                                )}
                                {(catalog.specialties?.length || catalog.specialty) && (
                                    <div>
                                        <label className="text-xs font-medium text-base-content/50">
                                            {(catalog.specialties?.length || 0) > 1 ? 'Especialidades' : 'Especialidade'}
                                        </label>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {catalog.specialties?.length
                                                ? catalog.specialties.map(cs => (
                                                    <span key={cs.specialty.id} className="badge badge-secondary badge-sm">
                                                        {cs.specialty.name}
                                                    </span>
                                                ))
                                                : catalog.specialty && (
                                                    <span className="badge badge-secondary badge-sm">
                                                        {catalog.specialty.name}
                                                    </span>
                                                )
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Instruções (Exames / Pedidos) */}
                            {catalog.instructions && (
                                <div className="mt-4 p-3 bg-info/5 rounded-lg border border-info/20">
                                    <label className="text-xs font-medium text-info flex items-center gap-1">
                                        <MdInfo className="text-sm" /> Instruções (Exame)
                                    </label>
                                    <p className="text-sm mt-1 whitespace-pre-wrap">{catalog.instructions}</p>
                                </div>
                            )}
                            {catalog.preInstructions && (
                                <div className="mt-4 p-3 bg-warning/5 rounded-lg border border-warning/20">
                                    <label className="text-xs font-medium text-warning">Instruções Pré-procedimento</label>
                                    <p className="text-sm mt-1 whitespace-pre-wrap">{catalog.preInstructions}</p>
                                </div>
                            )}
                            {catalog.postInstructions && (
                                <div className="mt-4 p-3 bg-success/5 rounded-lg border border-success/20">
                                    <label className="text-xs font-medium text-success">Instruções Pós-procedimento</label>
                                    <p className="text-sm mt-1 whitespace-pre-wrap">{catalog.postInstructions}</p>
                                </div>
                            )}

                            {/* Teleconsulta config */}
                            {catalog.category === 'TELECONSULTATION' && (
                                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {catalog.maxTeleDuration && (
                                        <div className="p-2 bg-base-200/50 rounded-lg text-center">
                                            <label className="text-xs text-base-content/50">Duração Máx.</label>
                                            <p className="text-sm font-semibold">{catalog.maxTeleDuration} min</p>
                                        </div>
                                    )}
                                    <div className="p-2 bg-base-200/50 rounded-lg text-center">
                                        <label className="text-xs text-base-content/50">Chat</label>
                                        <p className="text-sm font-semibold">{catalog.enableChat ? 'Sim' : 'Não'}</p>
                                    </div>
                                    <div className="p-2 bg-base-200/50 rounded-lg text-center">
                                        <label className="text-xs text-base-content/50">Compartilhar Tela</label>
                                        <p className="text-sm font-semibold">{catalog.enableScreenShare ? 'Sim' : 'Não'}</p>
                                    </div>
                                    <div className="p-2 bg-base-200/50 rounded-lg text-center">
                                        <label className="text-xs text-base-content/50">Gravação</label>
                                        <p className="text-sm font-semibold">{catalog.recordingEnabled ? 'Sim' : 'Não'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                    {/* Parceiros */}
                    {hasPartners && (
                        <div className="card bg-base-100 border border-base-300/50">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <h2 className="card-title text-base flex items-center gap-2">
                                        <MdBusiness className="text-lg text-secondary" />
                                        Parceiros
                                        <span className="badge badge-sm badge-secondary">{activePartners.length}</span>
                                    </h2>
                                </div>

                                <div className="overflow-x-auto mt-3">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Parceiro</th>
                                                <th>Preço Custo</th>
                                                <th>Preço Venda</th>
                                                <th>Markup</th>
                                                <th>Desc. Máx.</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {catalog.partners.map((partner: CatalogPartner) => (
                                                <tr key={partner.id} className={!partner.active ? 'opacity-40' : ''}>
                                                    <td>
                                                        <div>
                                                            <p className="font-medium">{partner.partner?.name || '-'}</p>
                                                            {partner.partner?.tradingName && (
                                                                <p className="text-xs text-base-content/50">{partner.partner.tradingName}</p>
                                                            )}
                                                            {(partner.partner?.city || partner.partner?.state) && (
                                                                <p className="text-xs text-base-content/50 flex items-center gap-1 mt-0.5">
                                                                    <MdLocationOn className="text-sm shrink-0" />
                                                                    {[partner.partner?.city, partner.partner?.state].filter(Boolean).join(' - ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{partner.price ? formatPrice(partner.price) : <span className="text-base-content/30">-</span>}</td>
                                                    <td className="font-semibold text-secondary">
                                                        {partner.sellingPrice ? formatPrice(partner.sellingPrice) : <span className="text-base-content/30">-</span>}
                                                    </td>
                                                    <td>{partner.markupPercentage ? `${partner.markupPercentage.toFixed(1)}%` : '-'}</td>
                                                    <td>{partner.maxDiscount ? formatPrice(partner.maxDiscount) : '-'}</td>
                                                    <td>
                                                        {partner.active
                                                            ? <span className="badge badge-xs badge-success">Ativo</span>
                                                            : <span className="badge badge-xs badge-ghost">Inativo</span>
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {catalog.partners.some(p => p.observations) && (
                                    <div className="mt-3 space-y-2">
                                        {catalog.partners.filter(p => p.observations).map(p => (
                                            <div key={p.id} className="text-xs p-2 bg-base-200/50 rounded-lg">
                                                <span className="font-medium">{p.partner?.name}: </span>
                                                <span className="text-base-content/70">{p.observations}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Profissionais */}
                    {hasProfessionals && (
                        <div className="card bg-base-100 border border-base-300/50">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <h2 className="card-title text-base flex items-center gap-2">
                                        <MdPerson className="text-lg text-secondary" />
                                        Profissionais
                                        <span className="badge badge-sm badge-secondary">{activeProfessionals.length}</span>
                                    </h2>
                                </div>

                                <div className="overflow-x-auto mt-3">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Profissional</th>
                                                <th>Registro</th>
                                                <th>Preço Custo</th>
                                                <th>Preço Venda</th>
                                                <th>Markup</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {catalog.professionals.map((prof: CatalogProfessional) => (
                                                <tr key={prof.id} className={!prof.active ? 'opacity-40' : ''}>
                                                    <td className="font-medium">{prof.professional?.name || '-'}</td>
                                                    <td className="text-xs">
                                                        {prof.professional?.registrationNumber
                                                            ? `${prof.professional.councilName || 'CRM'} ${prof.professional.registrationNumber}`
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td>{prof.price ? formatPrice(prof.price) : <span className="text-base-content/30">-</span>}</td>
                                                    <td className="font-semibold text-secondary">
                                                        {prof.sellingPrice ? formatPrice(prof.sellingPrice) : <span className="text-base-content/30">-</span>}
                                                    </td>
                                                    <td>{prof.markupPercentage ? `${prof.markupPercentage.toFixed(1)}%` : '-'}</td>
                                                    <td>
                                                        {prof.active
                                                            ? <span className="badge badge-xs badge-success">Ativo</span>
                                                            : <span className="badge badge-xs badge-ghost">Inativo</span>
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Variações */}
                    {hasVariations && (
                        <div className="card bg-base-100 border border-base-300/50">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <h2 className="card-title text-base flex items-center gap-2">
                                        <MdTune className="text-lg text-accent" />
                                        Variações
                                        <span className="badge badge-sm badge-accent">{activeVariations.length}</span>
                                    </h2>
                                </div>

                                <p className="text-xs text-base-content/50 mt-1">
                                    Cada variação tem preço próprio por filial e, quando escolhida no agendamento, substitui o preço base do item.
                                </p>

                                <div className="overflow-x-auto mt-3">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Variação</th>
                                                <th>Preço Custo</th>
                                                <th>Preço Venda</th>
                                                <th>Markup</th>
                                                <th>Desc. Máx.</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {catalog.variationChildren!.map((variation: Catalog) => {
                                                const vp = catalogCompanyPrice(variation, user?.companyId)
                                                return (
                                                    <tr key={variation.id} className={!variation.active ? 'opacity-40' : ''}>
                                                        <td>
                                                            <p className="font-medium">{variation.name}</p>
                                                            {variation.tussCode && (
                                                                <p className="text-xs text-base-content/50">TUSS {variation.tussCode}</p>
                                                            )}
                                                        </td>
                                                        <td>{vp.price ? formatPrice(vp.price) : <span className="text-base-content/30">-</span>}</td>
                                                        <td className="font-semibold text-secondary">
                                                            {vp.sellingPrice ? formatPrice(vp.sellingPrice) : <span className="text-base-content/30">-</span>}
                                                        </td>
                                                        <td>{vp.markupPercentage ? `${vp.markupPercentage.toFixed(1)}%` : '-'}</td>
                                                        <td>{vp.maxDiscount ? formatPrice(vp.maxDiscount) : '-'}</td>
                                                        <td>
                                                            {variation.active
                                                                ? <span className="badge badge-xs badge-success">Ativa</span>
                                                                : <span className="badge badge-xs badge-ghost">Inativa</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Exames Relacionados - apenas para PROCEDURE na saúde */}
                    {isHealth && catalog.category === 'PROCEDURE' && (
                        <div className="card bg-base-100 border border-base-300/50">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <h2 className="card-title text-base flex items-center gap-2">
                                        <MdScience className="text-lg text-info" />
                                        Exames Relacionados
                                        <span className="badge badge-sm badge-info">{catalog.catalogExams?.length || 0}</span>
                                    </h2>
                                </div>

                                {/* Busca para adicionar */}
                                {canUpdate && (
                                    <div className="mt-3 relative">
                                        <input
                                            type="text"
                                            className="input input-sm input-bordered w-full"
                                            placeholder="Buscar exame para adicionar..."
                                            value={examSearch}
                                            onChange={e => searchExams(e.target.value)}
                                        />
                                        {searchingExams && (
                                            <span className="loading loading-spinner loading-xs absolute right-3 top-2" />
                                        )}
                                        {examResults.length > 0 && (
                                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {examResults.map(exam => (
                                                    <button
                                                        key={exam.id}
                                                        className="w-full text-left px-3 py-2 hover:bg-base-200 flex items-center justify-between text-sm"
                                                        onClick={() => handleAddExam(exam.id)}
                                                        disabled={addingExam}
                                                    >
                                                        <div>
                                                            <p className="font-medium">{exam.name}</p>
                                                            {exam.catalogType && (
                                                                <span className="text-xs text-base-content/50">{exam.catalogType.name}</span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs font-semibold text-secondary">
                                                            {formatPrice(catalogBasePrice(exam, user?.companyId))}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Lista de exames */}
                                {catalog.catalogExams && catalog.catalogExams.length > 0 ? (
                                    <div className="overflow-x-auto mt-3">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr className="text-xs text-base-content/50">
                                                    <th>Exame</th>
                                                    <th>Tipo</th>
                                                    <th>Preço</th>
                                                    <th>Qtd</th>
                                                    <th>Cortesia</th>
                                                    {canUpdate && <th></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {catalog.catalogExams.map((ce: CatalogExam) => (
                                                    <tr key={ce.id} className={ce.complimentary ? 'opacity-60' : ''}>
                                                        <td className="font-medium">{ce.examCatalog.name}</td>
                                                        <td className="text-xs">{ce.examCatalog.catalogType?.name || '-'}</td>
                                                        <td className={ce.complimentary ? 'line-through text-base-content/40' : 'font-semibold text-secondary'}>
                                                            {formatPrice(ce.examCatalog.sellingPrice ?? ce.examCatalog.price)}
                                                        </td>
                                                        <td>{ce.quantity}</td>
                                                        <td>
                                                            {canUpdate ? (
                                                                <input
                                                                    type="checkbox"
                                                                    className="toggle toggle-xs toggle-info"
                                                                    checked={ce.complimentary}
                                                                    onChange={() => handleToggleComplimentary(ce)}
                                                                />
                                                            ) : (
                                                                ce.complimentary
                                                                    ? <span className="badge badge-xs badge-info">Cortesia</span>
                                                                    : '-'
                                                            )}
                                                        </td>
                                                        {canUpdate && (
                                                            <td>
                                                                <button
                                                                    className="btn btn-ghost btn-xs text-error"
                                                                    onClick={() => setConfirmRemoveExam(ce)}
                                                                    disabled={removingExamId === ce.examCatalogId}
                                                                >
                                                                    {removingExamId === ce.examCatalogId
                                                                        ? <span className="loading loading-spinner loading-xs" />
                                                                        : <MdDelete size={14} />
                                                                    }
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-sm text-base-content/40 mt-3">
                                        Nenhum exame atrelado a este procedimento.
                                    </p>
                                )}

                                {/* Observações dos exames */}
                                {catalog.catalogExams?.some(ce => ce.notes) && (
                                    <div className="mt-3 space-y-2">
                                        {catalog.catalogExams.filter(ce => ce.notes).map(ce => (
                                            <div key={ce.id} className="text-xs p-2 bg-base-200/50 rounded-lg">
                                                <span className="font-medium">{ce.examCatalog.name}: </span>
                                                <span className="text-base-content/70">{ce.notes}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Componentes do Pacote - apenas para PACKAGE */}
                    {catalog.category === 'PACKAGE' && (
                        <div className="card bg-base-100 border border-base-300/50">
                            <div className="card-body">
                                <h2 className="card-title text-base flex items-center gap-2">
                                    <MdScience className="text-lg text-success" />
                                    Componentes do Pacote
                                    <span className="badge badge-sm badge-success">{catalog.packageItems?.length || 0}</span>
                                </h2>
                                <p className="text-xs text-base-content/50 mt-1">
                                    O cliente paga o preço único do pacote.
                                    {isHealth
                                        ? ' Cada componente entra na guia com o seu próprio código TUSS.'
                                        : ''}
                                </p>

                                {catalog.packageItems && catalog.packageItems.length > 0 ? (
                                    <div className="overflow-x-auto mt-3">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr className="text-xs text-base-content/50">
                                                    <th>Componente</th>
                                                    <th>Categoria</th>
                                                    <th>Preço ref.</th>
                                                    <th>Qtd</th>
                                                    <th>Cortesia</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {catalog.packageItems.map(pi => (
                                                    <tr key={pi.id} className={pi.complimentary ? 'opacity-60' : ''}>
                                                        <td className="font-medium">
                                                            {pi.item?.name ?? '-'}
                                                        </td>
                                                        <td>
                                                            {/* Serviço e mercadoria são ambos item de catálogo:
                                                                o selo vem da categoria do próprio item. */}
                                                            <span className={`badge badge-xs ${pi.kind === 'PRODUCT' ? 'badge-info' : 'badge-ghost'}`}>
                                                                {pi.item
                                                                    ? categoryLabels[pi.item.category]
                                                                    : 'Componente'}
                                                            </span>
                                                        </td>

                                                        <td className={pi.complimentary ? 'line-through text-base-content/40' : 'font-semibold text-secondary'}>
                                                            {/* Preço de referência DESTA filial. */}
                                                            {pi.item
                                                                ? formatPrice(catalogBasePrice({ companyPrices: pi.item.companyPrices }, user?.companyId))
                                                                : '-'}
                                                        </td>
                                                        <td>{pi.quantity}</td>
                                                        <td>
                                                            {pi.complimentary
                                                                ? <span className="badge badge-xs badge-success">Cortesia</span>
                                                                : '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-sm text-base-content/40 mt-3">
                                        Nenhum componente neste pacote. Edite o pacote para adicionar.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Coluna lateral - Preços */}
                <div className="space-y-6">
                    <div className="card bg-base-100 border border-base-300/50">
                        <div className="card-body">
                            <h2 className="card-title text-base flex items-center gap-2">
                                <MdAttachMoney className="text-lg text-secondary" />
                                Preços
                            </h2>

                            <div className="space-y-4 mt-3">
                                <div className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg">
                                    <span className="text-sm text-base-content/70">Preço Base (Custo)</span>
                                    <span className="text-lg font-bold">{formatPrice(catalogCompanyPrice(catalog, user?.companyId).price)}</span>
                                </div>

                                {(catalogCompanyPrice(catalog, user?.companyId).sellingPrice ?? 0) != null && (catalogCompanyPrice(catalog, user?.companyId).sellingPrice ?? 0) > 0 && (
                                    <div className="flex items-center justify-between p-3 bg-secondary/5 rounded-lg border border-secondary/20">
                                        <span className="text-sm text-secondary font-medium">Preço de Venda</span>
                                        <span className="text-lg font-bold text-secondary">{formatPrice((catalogCompanyPrice(catalog, user?.companyId).sellingPrice ?? 0))}</span>
                                    </div>
                                )}

                                {(catalogCompanyPrice(catalog, user?.companyId).markupPercentage ?? 0) != null && (catalogCompanyPrice(catalog, user?.companyId).markupPercentage ?? 0) > 0 && (
                                    <div className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg">
                                        <span className="text-sm text-base-content/70">Markup</span>
                                        <span className="text-base font-semibold">{(catalogCompanyPrice(catalog, user?.companyId).markupPercentage ?? 0).toFixed(1)}%</span>
                                    </div>
                                )}

                                {(catalogCompanyPrice(catalog, user?.companyId).maxDiscount ?? 0) != null && (catalogCompanyPrice(catalog, user?.companyId).maxDiscount ?? 0) > 0 && (
                                    <div className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg">
                                        <span className="text-sm text-base-content/70">Desconto Máximo</span>
                                        <span className="text-base font-semibold">{formatPrice((catalogCompanyPrice(catalog, user?.companyId).maxDiscount ?? 0))}</span>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* Metadados */}
                    <div className="card bg-base-100 border border-base-300/50">
                        <div className="card-body">
                            <h2 className="card-title text-base">Detalhes</h2>
                            <div className="space-y-2 mt-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">ID</span>
                                    <span className="font-mono">{catalog.id}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">Categoria</span>
                                    <span>{categoryLabels[catalog.category]}</span>
                                </div>
                                {catalog.catalogType && (
                                    <div className="flex justify-between">
                                        <span className="text-base-content/50">Tipo</span>
                                        <span>{catalog.catalogType.name}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">Parceiros</span>
                                    <span>{activePartners.length} ativo(s)</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">Profissionais</span>
                                    <span>{activeProfessionals.length} ativo(s)</span>
                                </div>
                                {hasVariations && (
                                    <div className="flex justify-between">
                                        <span className="text-base-content/50">Variações</span>
                                        <span>{activeVariations.length} ativa(s)</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">Criado em</span>
                                    <span>{new Date(catalog.createdAt).toLocaleDateString('pt-BR')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-base-content/50">Atualizado em</span>
                                    <span>{new Date(catalog.updatedAt).toLocaleDateString('pt-BR')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={!!confirmRemoveExam}
                onClose={() => setConfirmRemoveExam(null)}
                onConfirm={handleRemoveExam}
                title="Remover Exame"
                message={`Deseja remover o exame "${confirmRemoveExam?.examCatalog.name}" deste procedimento?`}
                loading={removingExamId !== null}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
